import { TableIcon } from "@phosphor-icons/react/dist/csr/Table";
import {
  type ComponentProps,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Button } from "@volca/ui/components/ui/button";
import { count } from "@volca/ui/trades";
import { ChatTableDialog, type Grid, readGrid } from "./chat-table-view";

/** Narrower than this per column, a table is a column of fragments. */
const MIN_COLUMN_WIDTH = 88;

interface Shape {
  fits: boolean;
  columns: string[];
  rows: number;
}

/**
 * A table in a chat reply. A chat is read top to bottom: a table that fits
 * the reply's width (its cells wrapping) reads inline, as any other part of
 * the reply. One that does not, in a narrow panel, never scrolls sideways
 * inside the conversation: the reply shows what it is (its rows and first
 * columns) and opens it whole in a dialog, the way assistants beside a page
 * hand a result to a wider view (ChatTableDialog). Measured from a hidden
 * copy at its narrowest, again as the panel resizes or the reply streams in.
 */
export const ChatTable = (props: ComponentProps<"table">) => {
  const frame = useRef<HTMLDivElement>(null);
  const probe = useRef<HTMLTableElement>(null);
  const [shape, setShape] = useState<Shape>({
    fits: true,
    columns: [],
    rows: 0,
  });
  const [open, setOpen] = useState(false);
  // In a panel that is a native modal dialog (a full-screen chat on a
  // phone), the table opens inside it: the page sits behind the top layer.
  const [host, setHost] = useState<HTMLElement>();
  const [grid, setGrid] = useState<Grid>({ lead: "", columns: [], rows: [] });
  const show = () => {
    const table = probe.current;
    if (!table) return;
    setHost(frame.current?.closest<HTMLElement>("dialog[open]") ?? undefined);
    setGrid(readGrid(table, frame.current?.previousElementSibling ?? null));
    setOpen(true);
  };

  const measure = useCallback(() => {
    const table = probe.current;
    const width = frame.current?.clientWidth ?? 0;
    if (!table || !width) return;
    const columns = [...table.querySelectorAll("thead th")].map(
      (cell) => cell.textContent?.trim() ?? "",
    );
    const rows = table.querySelectorAll("tbody tr").length;
    const fits =
      table.offsetWidth <= width &&
      width / Math.max(columns.length, 1) >= MIN_COLUMN_WIDTH;
    setShape((current) =>
      current.fits === fits &&
      current.rows === rows &&
      current.columns.join("\u0000") === columns.join("\u0000")
        ? current
        : { fits, columns, rows },
    );
  }, []);

  // Every render: a streaming reply grows its table without resizing the panel.
  useLayoutEffect(measure);
  useLayoutEffect(() => {
    const element = frame.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  const { columns, rows } = shape;
  const named = columns.filter(Boolean);
  const more = named.length - 3;
  return (
    // As wide as the reply may be, not as its words: in a parent that shrinks
    // to fit (a chat column with items-start), w-screen asks for every pixel
    // and max-w-full caps it at what is there, so the fit is measured
    // against the panel, and again as the panel resizes.
    <div ref={frame} className="relative w-screen max-w-full">
      {/* At its narrowest (in no width), its cells wrapped. */}
      <div
        aria-hidden
        className="invisible absolute top-0 left-0 h-0 w-0 overflow-hidden"
      >
        <table {...props} ref={probe} />
      </div>
      {shape.fits ? (
        <table {...props} />
      ) : (
        <div className="not-typeset mt-3 flex items-center gap-3 rounded-lg border border-line bg-surface-raised px-3 py-2.5">
          <TableIcon aria-hidden className="size-5 shrink-0 text-subtle" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink">
              Table · {count(rows, "row")}
            </p>
            <p className="truncate text-xs text-subtle">
              {named.slice(0, 3).join(", ")}
              {more > 0 ? ` and ${count(more, "more column")}` : ""}
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={show}>
            Open
          </Button>
        </div>
      )}
      <ChatTableDialog
        grid={grid}
        open={open}
        onOpenChange={setOpen}
        container={host}
      />
    </div>
  );
};
